import { CoreButton, CoreModalDialog, MMPagination } from '@components'
import StatusBadge from '@components/StatusBadge'
import { breakpoints } from '@constants'
import RowIcon from '@images/icons/report-row-icon.svg?react'
import TransactionsExportDocumentIcon from '@images/icons/transaction-export-icon.svg?react'
import { formatCurrencyForDisplay } from '@utils'
import ApprovalStatus from '@views/expenseManagement/@elements/ApprovalStatus'
import { NoReportsComponent, ReportEditModal } from '@views/expenseManagement/components'
import { format } from 'date-fns'
import { paths, useCurrencies, useDownloadExpenseView } from 'mmfintech-backend-api'
import type { ReportResponse } from 'mmfintech-backend-api/types'
import { fixDateOnly, formatFloat, isValidArray, tr, useMatchMedia } from 'mmfintech-commons'
import type { UseFilterOutput, UsePaginationOutput } from 'mmfintech-commons/types'
import { DataTable } from 'mmfintech-portal-commons'
import { type MouseEvent, useCallback, useState } from 'react'

import './reportsList.scss'

const PAYMENT_STATES = ['APPROVED', 'COMPLETELY_PAID', 'PARTIALLY_PAID', 'UNPAID']

interface ReportsListProps {
  data: ReportResponse[]
  pagination: UsePaginationOutput
  filterValues: UseFilterOutput
  states: string[]
}

const ReportsList = (props: ReportsListProps) => {
  const { data, pagination, filterValues, states } = props

  const [reportCreateModalVisible, setReportCreateModalVisible] = useState<boolean>(false)

  const { getCurrencyPrecision } = useCurrencies()

  // const history = useHistory()
  const { downloadExpenseView } = useDownloadExpenseView()

  const isLarge = useMatchMedia({ breakpoint: breakpoints.lg, prefix: 'min' })
  const isXLarge = useMatchMedia({ breakpoint: breakpoints.xl, prefix: 'min' })

  const createModalOpen = useCallback(() => setReportCreateModalVisible(true), [])

  const createModalClose = useCallback(() => setReportCreateModalVisible(false), [])

  const formatDate = useCallback((date: string) => (date ? format(new Date(date), 'dd/MM/yyyy') : ''), [])

  const formatPeriod = useCallback(
    (fromDate: string, toDate: string) => {
      return `${formatDate(fromDate)} - ${formatDate(toDate)}`
    },
    [formatDate]
  )

  const handleReportRowClick = useCallback((e: MouseEvent<HTMLElement>, report: ReportResponse) => {
    e.preventDefault()
    e.stopPropagation()

    const targetPath = PAYMENT_STATES.includes(report?.state)
      ? paths.expenseManagement.payments(report?.id)
      : paths.expenseManagement.reportDetails(report?.id)

    window.open(targetPath, '_blank')
    // history.push(targetPath)
  }, [])

  const onExportClick = () => {
    downloadExpenseView({
      ...filterValues.prepare(),
      ...(filterValues.get('from') ? { fromDate: fixDateOnly(filterValues.get('from')) } : {}),
      ...(filterValues.get('to') ? { toDate: fixDateOnly(filterValues.get('to')) } : {}),
      ...(isValidArray(states) ? { states } : {})
    })
  }

  return (
    <>
      <div className='reports-table-wrapper table-container'>
        <DataTable.Table>
          {isValidArray(data) ? (
            <>
              {isLarge && (
                <DataTable.Head>
                  <DataTable.Row className='headRow'>
                    <DataTable.HeadCell colSpan={2}>{tr('FRONTEND.REPORTS.COLUMN_NAME', 'Name')}</DataTable.HeadCell>

                    {isXLarge && (
                      <DataTable.HeadCell>{tr('FRONTEND.REPORTS.COLUMN_PERIOD', 'Period')}</DataTable.HeadCell>
                    )}
                    <DataTable.HeadCell>{tr('FRONTEND.REPORTS.COLUMN_EXPENSES', 'Expenses')}</DataTable.HeadCell>
                    <DataTable.HeadCell>{tr('FRONTEND.REPORTS.COLUMN_DEPARTMENT', 'Department')}</DataTable.HeadCell>
                    <DataTable.HeadCell>
                      {tr('FRONTEND.REPORTS.COLUMN_APPROVAL_STATUS', 'Approval Status')}
                    </DataTable.HeadCell>
                    <DataTable.HeadCell className='text-right'>
                      {tr('FRONTEND.REPORTS.COLUMN_TOTAL', 'Total')}
                    </DataTable.HeadCell>
                  </DataTable.Row>
                </DataTable.Head>
              )}

              <DataTable.Body>
                {data.map((report: ReportResponse) => {
                  const { expenses, totalAmount, currency, id, name, fromDate, toDate, state, department } = report
                  return (
                    <DataTable.Row
                      key={id}
                      data-test={`open-report-${id}`}
                      onClick={e => handleReportRowClick(e, report)}
                    >
                      <DataTable.Cell className='table-cell name' colSpan={2} data-test='name'>
                        <RowIcon className='row-icon' />
                        <div>
                          {name}
                          {!isLarge && <span className='period'>{formatPeriod(fromDate, toDate)}</span>}
                        </div>
                      </DataTable.Cell>
                      {isXLarge && <DataTable.Cell data-test='period'>{formatPeriod(fromDate, toDate)}</DataTable.Cell>}
                      {isLarge && (
                        <>
                          <DataTable.Cell data-test='expenses'>{expenses?.length}</DataTable.Cell>
                          <DataTable.Cell data-test='department'>{department?.departmentName}</DataTable.Cell>
                          <DataTable.Cell data-test='status'>
                            <ApprovalStatus report={report} />
                          </DataTable.Cell>
                        </>
                      )}
                      <DataTable.Cell className='total text-right' data-test='total'>
                        <div className='amount'>
                          <span className='total-amount'>
                            {formatFloat(totalAmount, getCurrencyPrecision(currency))}
                            <span className='row-amount-currency'>{formatCurrencyForDisplay(currency)}</span>
                          </span>
                          {!isLarge && (
                            <span>
                              <StatusBadge status={state} />
                            </span>
                          )}
                        </div>
                      </DataTable.Cell>
                    </DataTable.Row>
                  )
                })}
              </DataTable.Body>
            </>
          ) : (
            <NoReportsComponent>
              <div className='no-reports-button-container'>
                <p>{tr('FRONTEND.REPORTS.NO_REPORTS_1', 'No reports for this filter.')}</p>
                <p>{tr('FRONTEND.REPORTS.NO_REPORTS_2', 'Choose another filter or create new report.')}</p>
                <CoreButton
                  text={tr('FRONTEND.REPORTS.CREATE_REPORT', 'Create Report')}
                  onClick={createModalOpen}
                  size='large'
                />
              </div>
            </NoReportsComponent>
          )}
        </DataTable.Table>
      </div>

      {isValidArray(data) && (
        <div className='report-list-pagination-container'>
          <div>
            <MMPagination {...pagination.register()} />
          </div>

          <CoreButton
            variation='tertiary'
            size='large'
            className='report-list-export-button'
            LeftIcon={<TransactionsExportDocumentIcon />}
            text={tr('FRONTEND.REPORTS.EXPORT', 'Export')}
            onClick={onExportClick}
            data-test='button-export-reports'
          />
        </div>
      )}

      <CoreModalDialog
        header={tr('FRONTEND.REPORTS.CREATE.CREATE_NEW_REPORT', 'Create New Report')}
        options={{ closeOnEscape: false, closeOnClickOutside: false }}
        visible={reportCreateModalVisible}
        content={<ReportEditModal onClose={createModalClose} />}
        onClose={createModalClose}
      />
    </>
  )
}

export default ReportsList
